import { FC, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import { PageHeader, Title } from './styled'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { Button } from 'views/components/UI/Buttons'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import { ActionRequestSuspensionPlatformAdmin } from 'views/components/UI/BluesiteTable/Actions/GridActions'
import { userSelector } from 'ducks/user'
import useGetCompanyDetail from 'hooks/company/useGetCompanyDetail/useGetCompanyDetail'
import { useSearchUsers } from 'hooks/users/useSearchUser/useSearchUser'
import { useNotification } from 'lib/context/notification.context'
import useSuspension from 'hooks/company/useSuspendPlatformAdmin/useSuspendPlatformAdmin'
import { USER_ROLE_ID } from 'constants/global.constants'
import { HEADERS_ADMIN_USERS } from './constants'
import _ from 'lodash'
import CreateUserModal from 'views/screens/super-admin/admin-platform-company/pages/create-admin-platform-modal'

const AdministratorsCompany: FC = () => {
  const { getError, getSuccess } = useNotification()
  const { suspendAdminUserData, isLoading, error, makeSuspendAdminUser } = useSuspension()
  const { getOneCompany } = useGetCompanyDetail()
  const { results, search, paginationUsers } = useSearchUsers()

  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const { companyId, userId } = useSelector(userSelector)
  const [isClosed, setIsClosed] = useState(true)
  const [lastAction, setLastAction] = useState<'suspend' | 'reactivate' | null>(null)

  function getAdmins() {
    search({
      companyId,
      role: [USER_ROLE_ID.platform_admin, USER_ROLE_ID.super_platform_admin],
      status: true,
      disableFilterByCompany: true,
    })
  }

  const handlePagination = (page: number) => {
    setCurrentPage(page)
    search({
      companyId,
      role: [USER_ROLE_ID.platform_admin, USER_ROLE_ID.super_platform_admin],
      status: true,
      disableFilterByCompany: true,
      page: page.toString(),
    })
  }

  const handleSuspend = async (userIdToSuspend: number) => {
    const dataSuspend = {
      userId: userIdToSuspend,
      statusRequest: 2, // Status for suspend
    }
    setLastAction('suspend')
    await makeSuspendAdminUser([dataSuspend])
  }

  const handleReactivate = async (userIdToReactivate: number) => {
    const dataReactivate = {
      userId: userIdToReactivate,
      statusRequest: 3, // Status for reactivate
    }
    setLastAction('reactivate')
    await makeSuspendAdminUser([dataReactivate])
  }

  useEffect(() => {
    if (suspendAdminUserData) {
      if (lastAction === 'suspend') {
        getSuccess('Usuario Suspendido con Éxito')
      } else if (lastAction === 'reactivate') {
        getSuccess('Usuario Activado con Éxito')
      }
      getAdmins()
      setLastAction(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suspendAdminUserData])

  useEffect(() => {
    getOneCompany(companyId)
    getAdmins()

    if (error) {
      getError('Hubo un error al intentar suspender al usuario')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (search.length) {
      search({
        companyId,
        role: [USER_ROLE_ID.platform_admin, USER_ROLE_ID.super_platform_admin],
        status: true,
        disableFilterByCompany: true,
        nameFilter: searchTerm,
      })
    } else {
      getAdmins()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const processedData = useMemo(() => {
    const filtered = results.length && _.filter(results, (obj) => obj.userId !== userId)
    if (!filtered) return []

    return filtered.map((user: any) => ({
      ...user,
      roleLabel: user.role === 'super_platform_admin' ? 'Administrador Líder' : 'Administrador',
      isBold: user.role === 'super_platform_admin',
    }))
  }, [results, userId])

  return (
    <MainLayout>
      <PageHeader>
        <Title>Administradores</Title>
        <Button
          type="button"
          onClick={() => {
            setIsClosed(false)
          }}
        >
          Agregar usuario
        </Button>
      </PageHeader>
      <BluesiteTable
        data={processedData}
        headers={HEADERS_ADMIN_USERS}
        searchBar={true}
        searchLabel="Administradores registrados"
        labelInput="Buscar administrador"
        onSearch={setSearchTerm}
        styleSearchBar={{ width: 'min(100%, 350px)' }}
        minimumColumnWidth={150}
        totalPages={paginationUsers.total_pages}
        currentPage={currentPage}
        handlePagination={handlePagination}
        ActionComponent={ActionRequestSuspensionPlatformAdmin}
        extraDataAction={{
          onRequestDeletePlatformAdmin: (e: number) => handleSuspend(e),
          onReactivatePlatformAdmin: (e: number) => handleReactivate(e),
        }}
      />
      <CreateUserModal
        companyId={companyId}
        handleCloseClick={() => setIsClosed(true)}
        openModal={isClosed}
        setOpenModal={setIsClosed}
        onFinished={() => {
          getAdmins()
          setIsClosed(true)
        }}
      />
      <ModalLoader isLoading={isLoading} />
    </MainLayout>
  )
}

export default AdministratorsCompany
